/**
 * Convert T-SQL (SQL Server) syntax to DuckDB-compatible SQL.
 *
 * Handles the most common patterns when pasting SQL Server queries into a
 * DuckDB context:
 *   - Bracket identifiers   [name]              → "name"
 *   - Three-part names      "db"."dbo"."tbl"    → "erp"."tbl"
 *   - Two-part dbo schema   "dbo"."tbl"         → "erp"."tbl"
 *   - SELECT TOP n          SELECT TOP 100 *    → SELECT * LIMIT 100
 *
 * Note: the regex-based approach handles the vast majority of real queries.
 * It does not parse the SQL; string literals that happen to contain brackets
 * (rare in warehouse queries) would also be converted.
 *
 * @param {string} sql              Raw SQL string
 * @param {string} warehouseSchema  DuckDB schema to substitute for SQL Server schemas (default: 'erp')
 * @returns {{ sql: string, changed: boolean, changes: string[] }}
 */
export function convertTSqlToDuckDB(sql, warehouseSchema = 'erp') {
  let out = sql;
  const changes = [];

  // 1. Bracket identifiers: [name] → "name"
  if (/\[/.test(out)) {
    out = out.replace(/\[([^\]]+)\]/g, '"$1"');
    changes.push('bracket identifiers [ ] → " "');
  }

  // 2. Three-part: "anything"."anything"."table" → "<schema>"."table"
  //    Covers [db].[dbo].[tbl] after step 1 converts brackets.
  if (/"[^"]+"\."[^"]+"\."[^"]+"/.test(out)) {
    out = out.replace(/"[^"]+"\."[^"]+"\."([^"]+)"/g, `"${warehouseSchema}"."$1"`);
    changes.push(`three-part names → "${warehouseSchema}"."table"`);
  }

  // 3. Two-part with SQL Server schema: "dbo"."table" → "<schema>"."table"
  if (/"dbo"\."[^"]+"/i.test(out)) {
    out = out.replace(/"dbo"\."([^"]+)"/gi, `"${warehouseSchema}"."$1"`);
    changes.push(`"dbo" schema → "${warehouseSchema}"`);
  }

  // 4. SELECT TOP n → SELECT … LIMIT n (TOP goes at the start, LIMIT at the end)
  const topMatch = out.match(/\bSELECT\s+TOP\s+(\d+)\b/i);
  if (topMatch) {
    const n = topMatch[1];
    out = out.replace(/\bSELECT\s+TOP\s+\d+\b/i, 'SELECT');
    out = out.trimEnd().replace(/;$/, '') + '\nLIMIT ' + n;
    changes.push(`SELECT TOP ${n} → SELECT … LIMIT ${n}`);
  }

  return { sql: out, changed: changes.length > 0, changes };
}
